/**
 * =====================================================
 * CompanyManager - Gestión de Empresas
 * =====================================================
 * Permite a usuarios PJ (y PH) registrar y gestionar empresas.
 * Integra consulta ARCA por CUIT, búsqueda de actividades
 * económicas, y edición de datos de empresa.
 * 
 * RESPONSIVE: diseñado mobile-first para funcionar en
 * pantallas desde 320px hasta desktop.
 * =====================================================
 */
import { useState, useEffect, useCallback, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { getUserErrorMessage } from "@/lib/userErrorMessage";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { EmptyState, Modal } from "@ava/shared/components/design-system";
import {
  Building2,
  Plus,
  CreditCard,
  Loader2,
  CheckCircle2,
  AlertCircle,
  MapPin,
  Briefcase,
  Edit,
  Search,
  X,
  Star,
  FileText,
  ChevronRight,
  ChevronsUpDown,
  Check,
} from "lucide-react";

// =====================================================
// Types
// =====================================================

interface ActividadEconomica {
  id: number;
  codigo: string;
  descripcion: string;
  descripcionLarga?: string;
}

interface ActividadSeleccionada {
  actividad: ActividadEconomica;
  esPrincipal: boolean;
}

// Nota: tipo: "PF" | "PJ" son los valores que devuelve la API externa AFIP/ARCA.
// "PF" equivale internamente a "Persona Humana" (PH) pero el valor no se modifica
// porque es el estándar de la API externa.
interface ContribuyenteData {
  cuit: string;
  tipo: "PF" | "PJ";
  nombre?: string;
  apellido?: string;
  razonSocial?: string;
  tipoPersona: string;
  estadoClave: string;
  domicilioFiscal?: {
    direccion?: string;
    localidad?: string;
    provincia?: string;
    codigoPostal?: string;
  };
  condicionIVA?: string;
  actividadPrincipal?: string;
  // TALAPP-1567: lista completa de actividades retornada por ARCA
  actividades?: Array<{
    idActividad: string;
    descripcion: string;
    orden?: number;
    periodo?: string;
  }>;
  encontrado: boolean;
  error?: string;
}

// TALAPP-1567: arma el string unificado de domicilio fiscal a partir de la respuesta de ARCA.
function formatDomicilioFromArca(d?: ContribuyenteData["domicilioFiscal"]): string {
  if (!d) return "";
  const parts: string[] = [];
  if (d.direccion) parts.push(d.direccion);
  if (d.localidad) {
    parts.push(d.codigoPostal ? `${d.localidad} (${d.codigoPostal})` : d.localidad);
  } else if (d.codigoPostal) {
    parts.push(d.codigoPostal);
  }
  if (d.provincia) parts.push(d.provincia);
  return parts.join(" - ");
}

interface Company {
  id: number;
  clientType: string;
  cuit: string | null;
  nombre: string | null;
  apellido: string | null;
  razonSocial: string | null;
  // TALAPP-1567: dirección fiscal unificada (texto libre)
  domicilio?: string | null;
  // Mantener split para retrocompatibilidad de lectura
  domicilioFiscal?: {
    calle: string | null;
    numero: string | null;
    piso: string | null;
    localidad: string | null;
    provincia: string | null;
    cp: string | null;
  };
  condicionIva: string | null;
  estadoAfip: string | null;
  createdAt: string | null;
}

// =====================================================
// Main Component
// =====================================================

export function CompanyManager({
  onSelectCompany,
  selectedCompanyId,
  openCreate,
  onOpenCreateChange,
}: {
  onSelectCompany?: (company: Company) => void;
  selectedCompanyId?: number | null;
  openCreate?: boolean;
  onOpenCreateChange?: (open: boolean) => void;
}) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  useEffect(() => {
    if (openCreate) setShowCreateDialog(true);
  }, [openCreate]);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);

  const utils = trpc.useUtils();
  const { data: companies, isLoading } = trpc.auth.myCompanies.useQuery();

  const handleCompanyCreated = () => {
    setShowCreateDialog(false);
    utils.auth.myCompanies.invalidate();
    toast.success("Empresa registrada exitosamente");
  };

  const handleCompanyUpdated = () => {
    setEditingCompany(null);
    utils.auth.myCompanies.invalidate();
    toast.success("Datos de empresa actualizados");
  };

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2].map((i) => (
          <div key={i} className="animate-pulse border rounded-lg p-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-gray-200 rounded-full" />
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-1/3" />
                <div className="h-3 bg-gray-200 rounded w-1/2" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div id="mis-empresas" className="space-y-4 scroll-mt-6">
      {/* Header - responsive: stack on mobile */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-foreground">Mis empresas</h3>
          <p className="text-sm text-muted-foreground">
            {companies && companies.length > 0
              ? `${companies.length} empresa${companies.length > 1 ? "s" : ""} registrada${companies.length > 1 ? "s" : ""}.`
              : "Registrá tu primera empresa para iniciar el onboarding."}
          </p>
        </div>
        <Dialog
          open={showCreateDialog}
          onOpenChange={(open) => {
            setShowCreateDialog(open);
            if (!open) onOpenCreateChange?.(false);
          }}
        >
          <DialogTrigger asChild>
            <Button size="sm" className="gap-2 w-full sm:w-auto">
              <Plus className="w-4 h-4" />
              Registrar empresa
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-[95vw] sm:max-w-lg max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <DialogHeader>
              <DialogTitle className="text-base sm:text-lg">Registrar Nueva Empresa</DialogTitle>
              <DialogDescription className="text-xs sm:text-sm">
                Ingresá el CUIT de la empresa para obtener los datos de ARCA automáticamente.
              </DialogDescription>
            </DialogHeader>
            <CreateCompanyForm onSuccess={handleCompanyCreated} onCancel={() => setShowCreateDialog(false)} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Company list — EmptyState del DS (kit v2 PJFlow pattern) */}
      {(!companies || companies.length === 0) && (
        <EmptyState
          icon={<Building2 className="w-6 h-6" />}
          title="No tenés empresas registradas."
          body={
            <>
              Usá el botón <strong className="font-semibold text-foreground">Registrar empresa</strong> para agregar una.
            </>
          }
        />
      )}

      {companies && companies.map((company) => (
        <CompanyCard
          key={company.id}
          company={company}
          isSelected={selectedCompanyId === company.id}
          onSelect={onSelectCompany ? () => onSelectCompany(company) : undefined}
          onEdit={() => setEditingCompany(company)}
        />
      ))}

      {/* Edit modal — del DS v2 (bottom-sheet en mobile, sentence case) */}
      <Modal
        open={!!editingCompany}
        onClose={() => setEditingCompany(null)}
        title="Editar empresa"
        subtitle={`Actualizá los datos de ${editingCompany?.razonSocial || "la empresa"}.`}
        icon={<Edit className="w-4 h-4" />}
        size="md"
      >
        {editingCompany && (
          <EditCompanyForm
            company={editingCompany}
            onSuccess={handleCompanyUpdated}
            onCancel={() => setEditingCompany(null)}
          />
        )}
      </Modal>
    </div>
  );
}

// =====================================================
// Company Card
// =====================================================

function CompanyCard({
  company,
  isSelected,
  onSelect,
  onEdit,
}: {
  company: Company;
  isSelected?: boolean;
  onSelect?: () => void;
  onEdit: () => void;
}) {
  const { data: detail } = trpc.auth.getCompanyDetail.useQuery(
    { clientId: company.id },
    { staleTime: 30000 }
  );

  // TALAPP-1567: usar el campo unificado, con fallback al legacy split
  const domicilio = company.domicilio || [
    company.domicilioFiscal?.calle,
    company.domicilioFiscal?.numero,
    company.domicilioFiscal?.localidad,
    company.domicilioFiscal?.provincia,
  ].filter(Boolean).join(", ");

  return (
    <Card
      className={`transition-all duration-200 ${
        onSelect ? "cursor-pointer hover:shadow-md" : ""
      } ${
        isSelected ? "ring-2 ring-primary shadow-md bg-primary/5" : ""
      } ${
        detail?.hasActiveRequest && onSelect ? "opacity-60" : ""
      }`}
      onClick={() => {
        if (onSelect && !detail?.hasActiveRequest) {
          onSelect();
        }
      }}
    >
      <CardContent className="py-3 px-3 sm:py-4 sm:px-4">
        <div className="flex items-start justify-between gap-2 sm:gap-3">
          <div className="flex items-start gap-2 sm:gap-3 flex-1 min-w-0">
            <div className="p-1.5 sm:p-2 rounded-lg bg-primary/10 shrink-0">
              <Building2 className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-1.5 sm:gap-2 flex-wrap">
                <h4 className="font-semibold text-xs sm:text-sm break-words">
                  {company.razonSocial || company.nombre || "Sin nombre"}
                </h4>
                {detail?.hasActiveRequest && (
                  <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] shrink-0">
                    <FileText className="w-3 h-3 mr-1" />
                    Solicitud activa
                  </Badge>
                )}
                {isSelected && (
                  <Badge className="bg-primary text-primary-foreground text-[10px] shrink-0">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    Seleccionada
                  </Badge>
                )}
              </div>
              <p className="text-[10px] sm:text-xs text-muted-foreground font-mono mt-0.5">
                CUIT: {formatCuitDisplay(company.cuit || "")}
              </p>
              {domicilio && (
                <p className="text-[10px] sm:text-xs text-muted-foreground mt-1 flex items-start gap-1">
                  <MapPin className="w-3 h-3 shrink-0 mt-0.5" />
                  <span className="break-words">{domicilio}</span>
                </p>
              )}
              {company.condicionIva && (
                <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5">
                  IVA: {company.condicionIva}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                onEdit();
              }}
              className="h-7 w-7 sm:h-8 sm:w-8 p-0"
            >
              <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </Button>
            {onSelect && !detail?.hasActiveRequest && (
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            )}
          </div>
        </div>

        {detail?.hasActiveRequest && onSelect && (
          <p className="text-[10px] sm:text-xs text-amber-600 mt-2 pl-8 sm:pl-11">
            Esta empresa ya tiene una solicitud activa. No se puede crear otra hasta que finalice.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// =====================================================
// Create Company Form
// =====================================================

function CreateCompanyForm({
  onSuccess,
  onCancel,
}: {
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [cuit, setCuit] = useState("");
  const [isLoadingCuit, setIsLoadingCuit] = useState(false);
  const [arcaData, setArcaData] = useState<ContribuyenteData | null>(null);
  const [arcaError, setArcaError] = useState<string | null>(null);
  const [razonSocial, setRazonSocial] = useState("");
  const [nombreComercial, setNombreComercial] = useState("");
  // TALAPP-1567: dirección fiscal unificada en un solo input
  const [domicilioFiscal, setDomicilioFiscal] = useState("");
  const [condicionIva, setCondicionIva] = useState("");
  const [actividades, setActividades] = useState<ActividadSeleccionada[]>([]);
  const [lastQueriedCuit, setLastQueriedCuit] = useState("");

  // TALAPP-1567: detectar si el CUIT corresponde a Persona Humana (PH).
  // arcaData.tipo devuelve "PF" (valor de la API AFIP/ARCA) para personas humanas —
  // ese valor es el estándar de la API externa y no se cambia.
  const cleanCuitDigits = cuit.replace(/\D/g, "");
  const cuitPrefix = parseInt(cleanCuitDigits.substring(0, 2), 10);
  const isPH = arcaData?.tipo === "PF" ||
    (!arcaData && [20, 23, 24, 25].includes(cuitPrefix));

  const createMutation = trpc.auth.createCompany.useMutation({
    onSuccess: () => onSuccess(),
    onError: (err) => toast.error(getUserErrorMessage(err)),
  });

  // TALAPP-1567: limpia todos los campos autocompletados
  const clearAutoFilledFields = useCallback(() => {
    setRazonSocial("");
    setNombreComercial("");
    setDomicilioFiscal("");
    setCondicionIva("");
    setActividades([]);
    setArcaData(null);
    setArcaError(null);
  }, []);

  const consultarCUIT = useCallback(async (cuitToQuery: string) => {
    if (cuitToQuery.length !== 11 || cuitToQuery === lastQueriedCuit) return;

    setIsLoadingCuit(true);
    setArcaError(null);
    setLastQueriedCuit(cuitToQuery);

    try {
      const response = await fetch(`/api/auth/cuit/${cuitToQuery}`);
      const result = await response.json();

      if (result.success && result.data) {
        const data = result.data as ContribuyenteData;
        setArcaData(data);
        if (data.encontrado) {
          // Razón Social — adapta para PH (la API AFIP/ARCA devuelve "PF" para personas humanas)
          if (data.tipo === "PF") {
            const fullName = [data.nombre, data.apellido].filter(Boolean).join(" ").trim();
            if (fullName) setRazonSocial(fullName);
          } else if (data.razonSocial) {
            setRazonSocial(data.razonSocial);
          }
          if (data.nombre && data.tipo !== "PF") setNombreComercial(data.nombre); // "PF" es valor de AFIP/ARCA para PH
          if (data.condicionIVA) setCondicionIva(data.condicionIVA);
          // Dirección Fiscal — un solo string
          setDomicilioFiscal(formatDomicilioFromArca(data.domicilioFiscal));

          // Actividades — autocompletar desde ARCA
          if (data.actividades && data.actividades.length > 0) {
            const codigos = data.actividades.map((a) => a.idActividad).filter(Boolean);
            if (codigos.length > 0) {
              try {
                const actRes = await fetch(`/api/actividades?codigos=${encodeURIComponent(codigos.join(","))}`);
                const actJson = await actRes.json();
                if (actJson.success && Array.isArray(actJson.data)) {
                  type ActividadRow = { id: number; codigo: string; descripcion: string; descripcionLarga?: string };
                  // TALAPP-1558: determinar UNA SOLA actividad principal.
                  // Usar la primera con orden===1; si no existe, la primera del array.
                  const principalIdx = data.actividades.findIndex((a) => a.orden === 1);
                  const idxPrincipal = principalIdx >= 0 ? principalIdx : 0;
                  const matched: ActividadSeleccionada[] = data.actividades
                    .map((arca, idx): ActividadSeleccionada | null => {
                      const row = (actJson.data as ActividadRow[]).find((r) => r.codigo === arca.idActividad);
                      if (!row) return null;
                      const actividad: ActividadEconomica = {
                        id: row.id,
                        codigo: row.codigo,
                        descripcion: row.descripcion,
                        ...(row.descripcionLarga !== undefined && { descripcionLarga: row.descripcionLarga }),
                      };
                      return {
                        actividad,
                        esPrincipal: idx === idxPrincipal,
                      };
                    })
                    .filter((x): x is ActividadSeleccionada => x !== null);
                  setActividades(matched);
                }
              } catch {
                // Silent: si falla el lookup, el usuario puede agregar manualmente
              }
            }
          }
        }
      } else {
        setArcaError(result.error || "Error consultando CUIT");
        setArcaData(null);
      }
    } catch (err: unknown) {
      setArcaError("Error de conexión");
      setArcaData(null);
    } finally {
      setIsLoadingCuit(false);
    }
  }, [lastQueriedCuit]);

  // TALAPP-1567: al cambiar el CUIT, limpiar lo autocompletado previamente
  useEffect(() => {
    const cleanCuit = cuit.replace(/\D/g, "");
    if (cleanCuit.length === 11) {
      // Si cambió respecto al último consultado, limpiamos antes de re-consultar
      if (cleanCuit !== lastQueriedCuit) {
        clearAutoFilledFields();
      }
      const timeoutId = setTimeout(() => consultarCUIT(cleanCuit), 500);
      return () => clearTimeout(timeoutId);
    } else {
      // CUIT incompleto: limpiar todo
      clearAutoFilledFields();
      setLastQueriedCuit("");
    }
  }, [cuit, consultarCUIT, clearAutoFilledFields, lastQueriedCuit]);

  const handleSubmit = () => {
    const cleanCuit = cuit.replace(/\D/g, "");
    if (cleanCuit.length !== 11) {
      toast.error("El CUIT debe tener 11 dígitos");
      return;
    }
    if (!razonSocial.trim() && !nombreComercial.trim()) {
      toast.error(isPH ? "Ingresá el nombre y apellido" : "Ingresá la razón social");
      return;
    }

    createMutation.mutate({
      cuit: cleanCuit,
      razonSocial: razonSocial || undefined,
      nombre: nombreComercial || undefined,
      apellido: undefined,
      domicilio: domicilioFiscal || undefined,
      condicionIva: condicionIva || undefined,
      estadoAfip: arcaData?.estadoClave || undefined,
      actividades: actividades.map((a) => ({
        actividadId: a.actividad.id,
        esPrincipal: a.esPrincipal,
      })),
    });
  };

  return (
    <div className="space-y-4">
      {/* CUIT Input */}
      <div className="space-y-1.5">
        <Label htmlFor="create-cuit" className="flex items-center gap-2 text-xs sm:text-sm">
          <CreditCard className="w-4 h-4 text-muted-foreground" />
          CUIT de la empresa
        </Label>
        <div className="relative">
          <Input
            id="create-cuit"
            type="text"
            placeholder="XX-XXXXXXXX-X"
            value={formatCuitDisplay(cuit)}
            onChange={(e) => setCuit(e.target.value.replace(/\D/g, ""))}
            disabled={isLoadingCuit || createMutation.isPending}
            maxLength={13}
            className="pr-10 text-sm"
          />
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {isLoadingCuit && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
            {!isLoadingCuit && arcaData?.encontrado && <CheckCircle2 className="w-4 h-4 text-green-500" />}
            {!isLoadingCuit && arcaError && <AlertCircle className="w-4 h-4 text-yellow-500" />}
          </div>
        </div>

        {arcaData?.encontrado && (
          <div className="text-xs p-2 bg-green-50 border border-green-200 rounded-md space-y-0.5">
            <p className="font-medium text-green-700">Datos verificados en ARCA</p>
            {arcaData.condicionIVA && <p className="text-green-600">IVA: {arcaData.condicionIVA}</p>}
            {arcaData.estadoClave && <p className="text-green-600">Estado: {arcaData.estadoClave}</p>}
            {arcaData.actividadPrincipal && (
              <p className="text-green-600 break-words">Actividad: {arcaData.actividadPrincipal}</p>
            )}
          </div>
        )}

        {arcaError && cuit.replace(/\D/g, "").length === 11 && (
          <div className="text-xs p-2 bg-yellow-50 border border-yellow-200 rounded-md">
            <p className="text-yellow-700">{arcaError}</p>
            <p className="text-yellow-600">Podés continuar con el ingreso manual.</p>
          </div>
        )}

        {!arcaData && !arcaError && !isLoadingCuit && (
          <p className="text-xs text-muted-foreground">Ingresá el CUIT de 11 dígitos para consultar datos en ARCA</p>
        )}
      </div>

      {/* Razón Social / Nombre y Apellido (TALAPP-1567: adapta para PH) */}
      <div className="space-y-1.5">
        <Label htmlFor="create-razon" className="flex items-center gap-2 text-xs sm:text-sm">
          <Building2 className="w-4 h-4 text-muted-foreground" />
          {isPH ? "Nombre y Apellido" : "Razón Social"}
        </Label>
        <Input
          id="create-razon"
          placeholder={isPH ? "Juan Pérez" : "Empresa S.A."}
          value={razonSocial}
          onChange={(e) => setRazonSocial(e.target.value)}
          disabled={createMutation.isPending}
          className="text-sm"
        />
      </div>

      {/* Nombre comercial (sólo para PJ) */}
      {!isPH && (
        <div className="space-y-1.5">
          <Label htmlFor="create-nombre" className="text-xs sm:text-sm">Nombre comercial</Label>
          <Input
            id="create-nombre"
            placeholder="Nombre comercial o fantasía (opcional)"
            value={nombreComercial}
            onChange={(e) => setNombreComercial(e.target.value)}
            disabled={createMutation.isPending}
            className="text-sm"
          />
        </div>
      )}

      {/* Dirección Fiscal — único input (TALAPP-1567) */}
      <div className="space-y-1.5">
        <Label htmlFor="create-domicilio" className="flex items-center gap-2 text-xs sm:text-sm">
          <MapPin className="w-4 h-4 text-muted-foreground" />
          Dirección Fiscal
        </Label>
        <Input
          id="create-domicilio"
          placeholder="Calle Número - Localidad (Código Postal) - Provincia"
          value={domicilioFiscal}
          onChange={(e) => setDomicilioFiscal(e.target.value)}
          disabled={createMutation.isPending}
          className="text-sm"
        />
        <p className="text-[10px] sm:text-xs text-muted-foreground">
          Formato esperado: <span className="font-mono">Calle Número - Localidad (Código Postal) - Provincia</span>
        </p>
      </div>

      {/* Actividades */}
      <div className="space-y-1.5">
        <Label className="flex items-center gap-2 text-xs sm:text-sm">
          <Briefcase className="w-4 h-4 text-muted-foreground" />
          Actividades Económicas
        </Label>
        <ActividadBuscador
          onSelect={(actividad) => {
            if (actividades.some((a) => a.actividad.id === actividad.id)) return;
            const esPrincipal = actividades.length === 0;
            setActividades([...actividades, { actividad, esPrincipal }]);
          }}
          excludeIds={actividades.map((a) => a.actividad.id)}
          disabled={createMutation.isPending}
        />

        {actividades.length > 0 && (
          <div className="space-y-1.5 mt-2">
            {actividades.map((item) => (
              <ActividadItem
                key={item.actividad.id}
                item={item}
                onMakePrincipal={() => {
                  setActividades(actividades.map((a) => ({
                    ...a,
                    esPrincipal: a.actividad.id === item.actividad.id,
                  })));
                }}
                onRemove={() => {
                  const nuevas = actividades.filter((a) => a.actividad.id !== item.actividad.id);
                  if (nuevas.length > 0 && !nuevas.some((a) => a.esPrincipal)) {
                    nuevas[0].esPrincipal = true;
                  }
                  setActividades(nuevas);
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Actions - responsive */}
      <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={createMutation.isPending} className="w-full sm:w-auto">
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={createMutation.isPending || cuit.replace(/\D/g, "").length !== 11}
          className="w-full sm:w-auto"
        >
          {createMutation.isPending ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Registrando...</>
          ) : (
            <><Building2 className="w-4 h-4 mr-2" />Registrar empresa</>
          )}
        </Button>
      </DialogFooter>
    </div>
  );
}

// =====================================================
// Edit Company Form
// =====================================================

function EditCompanyForm({
  company,
  onSuccess,
  onCancel,
}: {
  company: Company;
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [razonSocial, setRazonSocial] = useState(company.razonSocial || "");
  const [nombreComercial, setNombreComercial] = useState(company.nombre || "");
  // TALAPP-1567: dirección fiscal unificada (texto libre)
  const [domicilioFiscal, setDomicilioFiscal] = useState(company.domicilio || "");

  // TALAPP-1567: detectar si la empresa es Persona Humana (PH)
  const isPH = company.clientType === "PERSONA_HUMANA";

  const { data: detail } = trpc.auth.getCompanyDetail.useQuery({ clientId: company.id });
  const [actividades, setActividades] = useState<ActividadSeleccionada[]>([]);

  // Load existing actividades
  useEffect(() => {
    if (detail?.actividades) {
      setActividades(
        detail.actividades.map((a) => ({
          actividad: {
            id: a.actividadId,
            codigo: a.codigo || "",
            descripcion: a.descripcion || "",
          },
          esPrincipal: a.esPrincipal || false,
        }))
      );
    }
  }, [detail]);

  const updateMutation = trpc.auth.updateCompany.useMutation({
    onSuccess: () => onSuccess(),
    onError: (err) => toast.error(getUserErrorMessage(err)),
  });

  const handleSubmit = () => {
    updateMutation.mutate({
      clientId: company.id,
      razonSocial: razonSocial || undefined,
      nombre: nombreComercial || undefined,
      apellido: undefined,
      domicilio: domicilioFiscal || undefined,
      actividades: actividades.map((a) => ({
        actividadId: a.actividad.id,
        esPrincipal: a.esPrincipal,
      })),
    });
  };

  return (
    <div className="space-y-4">
      {/* CUIT (read-only) */}
      <div className="space-y-1.5">
        <Label className="flex items-center gap-2 text-xs sm:text-sm">
          <CreditCard className="w-4 h-4 text-muted-foreground" />
          CUIT
        </Label>
        <Input value={formatCuitDisplay(company.cuit || "")} disabled className="bg-muted text-sm" />
        <p className="text-[10px] sm:text-xs text-muted-foreground">El CUIT no se puede modificar.</p>
      </div>

      {/* Razón Social / Nombre y Apellido (TALAPP-1567: adapta para PH) */}
      <div className="space-y-1.5">
        <Label htmlFor="edit-razon" className="text-xs sm:text-sm">
          {isPH ? "Nombre y Apellido" : "Razón Social"}
        </Label>
        <Input
          id="edit-razon"
          placeholder={isPH ? "Juan Pérez" : "Empresa S.A."}
          value={razonSocial}
          onChange={(e) => setRazonSocial(e.target.value)}
          disabled={updateMutation.isPending}
          className="text-sm"
        />
      </div>

      {/* Nombre comercial (sólo para PJ) */}
      {!isPH && (
        <div className="space-y-1.5">
          <Label htmlFor="edit-nombre" className="text-xs sm:text-sm">Nombre comercial</Label>
          <Input
            id="edit-nombre"
            placeholder="Nombre comercial o fantasía (opcional)"
            value={nombreComercial}
            onChange={(e) => setNombreComercial(e.target.value)}
            disabled={updateMutation.isPending}
            className="text-sm"
          />
        </div>
      )}

      {/* Dirección Fiscal — único input (TALAPP-1567) */}
      <div className="space-y-1.5">
        <Label htmlFor="edit-domicilio" className="flex items-center gap-2 text-xs sm:text-sm">
          <MapPin className="w-4 h-4 text-muted-foreground" />
          Dirección Fiscal
        </Label>
        <Input
          id="edit-domicilio"
          placeholder="Calle Número - Localidad (Código Postal) - Provincia"
          value={domicilioFiscal}
          onChange={(e) => setDomicilioFiscal(e.target.value)}
          disabled={updateMutation.isPending}
          className="text-sm"
        />
        <p className="text-[10px] sm:text-xs text-muted-foreground">
          Formato esperado: <span className="font-mono">Calle Número - Localidad (Código Postal) - Provincia</span>
        </p>
      </div>

      {/* Actividades */}
      <div className="space-y-1.5">
        <Label className="flex items-center gap-2 text-xs sm:text-sm">
          <Briefcase className="w-4 h-4 text-muted-foreground" />
          Actividades Económicas
        </Label>
        <ActividadBuscador
          onSelect={(actividad) => {
            if (actividades.some((a) => a.actividad.id === actividad.id)) return;
            const esPrincipal = actividades.length === 0;
            setActividades([...actividades, { actividad, esPrincipal }]);
          }}
          excludeIds={actividades.map((a) => a.actividad.id)}
          disabled={updateMutation.isPending}
        />

        {actividades.length > 0 && (
          <div className="space-y-1.5 mt-2">
            {actividades.map((item) => (
              <ActividadItem
                key={item.actividad.id}
                item={item}
                onMakePrincipal={() => {
                  setActividades(actividades.map((a) => ({
                    ...a,
                    esPrincipal: a.actividad.id === item.actividad.id,
                  })));
                }}
                onRemove={() => {
                  const nuevas = actividades.filter((a) => a.actividad.id !== item.actividad.id);
                  if (nuevas.length > 0 && !nuevas.some((a) => a.esPrincipal)) {
                    nuevas[0].esPrincipal = true;
                  }
                  setActividades(nuevas);
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Actions - responsive */}
      <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
        <Button variant="outline" onClick={onCancel} disabled={updateMutation.isPending} className="w-full sm:w-auto">
          Cancelar
        </Button>
        <Button onClick={handleSubmit} disabled={updateMutation.isPending} className="w-full sm:w-auto">
          {updateMutation.isPending ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</>
          ) : (
            "Guardar Cambios"
          )}
        </Button>
      </DialogFooter>
    </div>
  );
}

// =====================================================
// Provincia Combobox (searchable dropdown)
// =====================================================

function ProvinciaCombobox({
  value,
  provincias,
  onSelect,
  disabled,
}: {
  value: string;
  provincias: { id: number; nombre: string }[] | undefined;
  onSelect: (value: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className="w-full justify-between text-sm font-normal h-9"
        >
          <span className={cn("truncate", !value && "text-muted-foreground")}>
            {value || "Provincia"}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput placeholder="Buscar provincia..." />
          <CommandList>
            <CommandEmpty>No se encontró la provincia.</CommandEmpty>
            <CommandGroup>
              {provincias?.map((p) => (
                <CommandItem
                  key={p.id}
                  value={p.nombre}
                  onSelect={(currentValue) => {
                    onSelect(currentValue === value ? "" : currentValue);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === p.nombre ? "opacity-100" : "opacity-0"
                    )}
                  />
                  {p.nombre}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

// =====================================================
// Actividad Item (shared between Create and Edit)
// =====================================================

function ActividadItem({
  item,
  onMakePrincipal,
  onRemove,
}: {
  item: ActividadSeleccionada;
  onMakePrincipal: () => void;
  onRemove: () => void;
}) {
  return (
    <div
      className={`p-2 rounded-md border text-xs ${
        item.esPrincipal ? "bg-primary/5 border-primary/30" : "bg-muted/30"
      }`}
    >
      {/* Row 1: code + actions */}
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-muted-foreground bg-muted px-1 rounded shrink-0">
          {item.actividad.codigo}
        </span>
        <div className="flex items-center gap-1.5 shrink-0">
          {item.esPrincipal && (
            <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/20 px-1.5">
              <Star className="w-2.5 h-2.5 mr-0.5" />
              Principal
            </Badge>
          )}
          {!item.esPrincipal && (
            <button
              type="button"
              onClick={onMakePrincipal}
              className="text-[10px] text-muted-foreground hover:text-primary whitespace-nowrap"
            >
              Hacer principal
            </button>
          )}
          <button
            type="button"
            onClick={onRemove}
            className="text-muted-foreground hover:text-red-500 p-0.5"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
      {/* Row 2: full description, wrapping */}
      <p className="mt-1 text-xs text-foreground/80 break-words leading-relaxed">
        {item.actividad.descripcion}
      </p>
    </div>
  );
}

// =====================================================
// Actividad Buscador
// =====================================================

function ActividadBuscador({
  onSelect,
  excludeIds,
  disabled,
}: {
  onSelect: (a: ActividadEconomica) => void;
  excludeIds: number[];
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState<ActividadEconomica[]>([]);
  const [loading, setLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    if (search.length < 2) {
      setResults([]);
      return;
    }

    const controller = new AbortController();
    setLoading(true);
    setHighlightIndex(-1);

    const timeoutId = setTimeout(async () => {
      try {
        const response = await fetch(
          `/api/actividades?q=${encodeURIComponent(search)}&limit=30`,
          { signal: controller.signal }
        );
        const data = await response.json();
        if (data.success) {
          setResults(data.data.filter((a: ActividadEconomica) => !excludeIds.includes(a.id)));
        }
      } catch (err: unknown) {
        if (err instanceof Error && err.name !== "AbortError") {
          // silenced
        }
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [search, open, excludeIds]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
    } else if (e.key === "Enter" && highlightIndex >= 0) {
      e.preventDefault();
      onSelect(results[highlightIndex]);
      setSearch("");
      setResults([]);
      setHighlightIndex(-1);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const item = listRef.current.children[highlightIndex] as HTMLElement;
      if (item) item.scrollIntoView({ block: "nearest" });
    }
  }, [highlightIndex]);

  return (
    <div ref={containerRef} className="relative">
      <div
        className={`flex items-center border rounded-md bg-background transition-colors ${
          open ? "border-primary ring-1 ring-primary" : "border-input"
        }`}
      >
        <Search className="w-4 h-4 text-muted-foreground ml-3 shrink-0" />
        <Input
          ref={inputRef}
          type="text"
          placeholder="Buscar por código o descripción..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-sm"
        />
        {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground mr-3 shrink-0" />}
      </div>

      {open && (search.length >= 2 || loading) && (
        <div
          ref={listRef}
          className="absolute z-50 left-0 right-0 mt-1 bg-popover border rounded-md shadow-lg max-h-48 overflow-auto"
        >
          {results.length === 0 && !loading && search.length >= 2 && (
            <div className="px-3 py-3 text-center text-xs text-muted-foreground">
              No se encontraron actividades para "{search}"
            </div>
          )}
          {results.map((actividad, index) => (
            <button
              key={actividad.id}
              type="button"
              className={`w-full text-left px-3 py-2 text-xs border-b last:border-0 transition-colors cursor-pointer ${
                index === highlightIndex ? "bg-accent text-accent-foreground" : "hover:bg-accent/50"
              }`}
              onClick={() => {
                onSelect(actividad);
                setSearch("");
                setResults([]);
                setHighlightIndex(-1);
                inputRef.current?.focus();
              }}
              onMouseEnter={() => setHighlightIndex(index)}
            >
              <span className="font-mono text-muted-foreground shrink-0 bg-muted px-1 rounded mr-2">
                {actividad.codigo}
              </span>
              <span className="break-words">{actividad.descripcion}</span>
            </button>
          ))}
          {loading && (
            <div className="px-3 py-3 text-center text-xs text-muted-foreground">
              <Loader2 className="w-3 h-3 animate-spin inline mr-1" />
              Buscando...
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// =====================================================
// Helpers
// =====================================================

function formatCuitDisplay(value: string): string {
  const numbers = value.replace(/\D/g, "").slice(0, 11);
  if (numbers.length <= 2) return numbers;
  if (numbers.length <= 10) return `${numbers.slice(0, 2)}-${numbers.slice(2)}`;
  return `${numbers.slice(0, 2)}-${numbers.slice(2, 10)}-${numbers.slice(10)}`;
}
